import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../lib/auth';
import { usePayers, useAddNetworkPlan } from '../api/hooks';
import { Plus, ShieldCheck } from 'lucide-react';
import { toast } from '../lib/aiToast';

const STATUSES = [
  { k: 'in_network', label: 'In-network', on: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
  { k: 'out_of_network', label: 'Out-of-network', on: 'border-rose-300 bg-rose-50 text-rose-700' },
  { k: 'unknown', label: 'Unknown', on: 'border-amber-300 bg-amber-50 text-amber-700' },
];

// Manual plan add (gap 3.3). Adds a plan to the network library directly, tagged
// source: 'manual' with provenance — a trusted assertion, like a CSV import.
export default function NetworkAddPlan({ open, onClose, practiceId = 'prac_001' }) {
  const { session } = useAuth();
  const { data: payers = [] } = usePayers();
  const addPlan = useAddNetworkPlan();
  const [payerId, setPayerId] = useState('');
  const [planName, setPlanName] = useState('');
  const [planType, setPlanType] = useState('');
  const [status, setStatus] = useState('in_network');

  useEffect(() => {
    if (open) { setPayerId(''); setPlanName(''); setPlanType(''); setStatus('in_network'); }
  }, [open]);

  const canSubmit = payerId && planName.trim() && !addPlan.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    const name = planName.trim();
    try {
      await addPlan.mutateAsync({
        practiceId,
        payerId,
        planName: name,
        planType: planType.trim() || null,
        status,
        source: 'manual',
        addedBy: session?.user?.name || 'Front desk',
        evidence: null,
      });
      toast.success(`Added ${name} to the network library`);
      onClose?.();
    } catch (e) {
      toast.error('Could not add plan');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-brand-600" />Add plan to network</DialogTitle>
          <DialogDescription>Manually add a plan to your network library. Recorded with source and who added it.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          <div className="grid gap-1.5">
            <Label>Payer</Label>
            <Select value={payerId} onValueChange={setPayerId}>
              <SelectTrigger><SelectValue placeholder="Select payer" /></SelectTrigger>
              <SelectContent>{payers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ap-name">Plan name</Label>
            <Input id="ap-name" value={planName} onChange={(e) => setPlanName(e.target.value)} placeholder="e.g. Aetna Choice POS II" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ap-type">Plan type <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input id="ap-type" value={planType} onChange={(e) => setPlanType(e.target.value)} placeholder="PPO / HMO / EPO / POS" />
          </div>
          <div className="grid gap-1.5">
            <Label>Network status</Label>
            <div className="grid grid-cols-3 gap-2">
              {STATUSES.map((s) => (
                <button key={s.k} type="button" onClick={() => setStatus(s.k)}
                  className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${status === s.k ? s.on : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-[11px] text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5 mt-px shrink-0" />
            Tagged as a manual entry with your name and the date — a trusted addition that won't route for phone verification.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose?.()}>Cancel</Button>
          <Button className="bg-brand-600 hover:bg-brand-700 text-white" disabled={!canSubmit} onClick={submit}>Add plan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
